import axios, { type AxiosError } from 'axios';
import ssoAuth from '@/config/sso';
import {
  captureCallSite,
  normalizePath,
  reportHttpError,
} from '@/config/sentry/helpers';

export { AxiosError } from 'axios';

// Axios Client for APIs
export const axiosApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { Accept: 'application/json' },
});

type ConfigWithCallSite = { callSite?: string };

const prepareRequest = <T extends { headers: Record<string, unknown> }>(
  config: T,
): T => {
  config.headers['Authorization'] = ssoAuth.getToken();
  (config as ConfigWithCallSite).callSite = captureCallSite(prepareRequest);

  return config;
};

axiosApi.interceptors.request.use(
  prepareRequest,
  error => Promise.reject(error),
  { synchronous: true },
);

export const publicAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { Accept: 'application/json' },
});

publicAxiosApi.interceptors.request.use(
  config => {
    (config as ConfigWithCallSite).callSite = captureCallSite(prepareRequest);
    return config;
  },
  error => Promise.reject(error),
  { synchronous: true },
);

const UNREPORTED_STATUSES = [401];

const reportResponseError = (error: AxiosError) => {
  const status = error.response?.status;
  if (!axios.isCancel(error) && !UNREPORTED_STATUSES.includes(status ?? 0)) {
    const method = error.config?.method?.toUpperCase();
    const path = normalizePath(error.config?.url);
    reportHttpError({
      source: 'axios',
      message: `HTTP ${status ?? 'network error'} on ${method} ${path}`,
      method,
      url: axios.getUri(error.config),
      status,
      headers: error.response?.headers,
      responseData: error.response?.data,
      level: status && status < 500 ? 'warning' : 'error',
      fingerprint: ['axios-error', method ?? '', path, String(status)],
      cause: error,
      callSite: (error.config as ConfigWithCallSite | undefined)?.callSite,
    });
  }
  return Promise.reject(error);
};

axiosApi.interceptors.response.use(response => response, reportResponseError);
publicAxiosApi.interceptors.response.use(
  response => response,
  reportResponseError,
);
